import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import API, { getActiveBranch } from "./apiBase";
import "./MenuQrModal.css";

export default function MenuQrModal({
  open,
  onClose,
  branchCode,
  setBranchCode,
  serverLocked,
  hostLockedBranch,
  BRANCH_OPTIONS = [],
  BRANCH_LABEL_MAP = {},
  API_BASE,
}) {
  const [menuToken, setMenuToken] = useState("");
  const [resolvedBranch, setResolvedBranch] = useState("");
  const [tokenExpiresIn, setTokenExpiresIn] = useState(0);
  const [tokenWindowSeconds, setTokenWindowSeconds] = useState(0);
  const [resetTime, setResetTime] = useState("23:59:59");
  const [resetMode, setResetMode] = useState("daily");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState("");

  const tokenTimerRef = useRef(null);
  const tokenRefreshRef = useRef(null);

  const getResponsiveQrSize = useCallback(() => {
    if (typeof window === "undefined") return 320;

    const vw = window.innerWidth || 390;
    const vh = window.innerHeight || 844;
    const shortest = Math.min(vw, vh);

    if (vw >= 1400) return 520;
    if (vw >= 1200) return 480;
    if (vw >= 992) return 440;
    if (vw >= 768) return 380;
    return Math.max(240, Math.min(360, Math.floor(shortest - 64)));
  }, []);

  const [qrSize, setQrSize] = useState(getResponsiveQrSize);

  useEffect(() => {
    const onResize = () => setQrSize(getResponsiveQrSize());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [getResponsiveQrSize]);

  const normalizeBase = useCallback((value) => {
    return String(value || "").trim().replace(/\/+$/g, "");
  }, []);

  const apiBase = useMemo(() => {
    if (API_BASE) return normalizeBase(API_BASE);
    if (API) return normalizeBase(API);

    if (typeof window !== "undefined") {
      const host = window.location.hostname;

      if (
        host === "bamboome.me" ||
        host === "www.bamboome.me" ||
        host === "api.bamboome.me"
      ) {
        return "https://api.bamboome.me";
      }

      return normalizeBase(window.location.origin);
    }

    return "";
  }, [API_BASE, normalizeBase]);

  const requestedBranch =
    (serverLocked ? "" : branchCode) ||
    hostLockedBranch ||
    getActiveBranch() ||
    "jounieh";

  const activeBranch = resolvedBranch || requestedBranch || "jounieh";

  const activeBranchLabel = useMemo(() => {
    return BRANCH_LABEL_MAP?.[activeBranch] || activeBranch || "Branch";
  }, [activeBranch, BRANCH_LABEL_MAP]);

  const formatExpires = (sec) => {
    const s = Math.max(0, Number(sec || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
    }
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  const clearTokenState = useCallback(() => {
    setMenuToken("");
    setResolvedBranch("");
    setTokenExpiresIn(0);
    setTokenWindowSeconds(0);
    setResetTime("23:59:59");
    setResetMode("daily");
  }, []);

  const clearTimers = useCallback(() => {
    if (tokenTimerRef.current) {
      clearInterval(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }
    if (tokenRefreshRef.current) {
      clearTimeout(tokenRefreshRef.current);
      tokenRefreshRef.current = null;
    }
  }, []);

  const buildMenuFileUrl = useCallback(
    (branch, token) => {
      if (!apiBase || !branch || !token) return "";

      try {
        const url = new URL("/menu-page", apiBase);
        url.searchParams.set("branch", branch);
        url.searchParams.set("t", token);
        return url.toString();
      } catch {
        return `${apiBase}/menu-page?branch=${encodeURIComponent(branch)}&t=${encodeURIComponent(token)}`;
      }
    },
    [apiBase]
  );

  const fetchMenuToken = useCallback(async () => {
    if (!open) return;

    if (!apiBase) {
      setTokenError("API base is missing.");
      return;
    }

    setTokenLoading(true);
    setTokenError("");

    try {
      const headers = {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      };

      const targetBranch =
        !serverLocked && branchCode
          ? String(branchCode).trim().toLowerCase()
          : String(hostLockedBranch || getActiveBranch() || "").trim().toLowerCase();

      if (!serverLocked && targetBranch) {
        headers["x-branch"] = targetBranch;
      }

      let url = "";
      try {
        const u = new URL("/menu-token", apiBase);
        if (!serverLocked && targetBranch) {
          u.searchParams.set("branch", targetBranch);
        }
        u.searchParams.set("_ts", String(Date.now()));
        url = u.toString();
      } catch {
        url =
          !serverLocked && targetBranch
            ? `${apiBase}/menu-token?branch=${encodeURIComponent(targetBranch)}&_ts=${Date.now()}`
            : `${apiBase}/menu-token?_ts=${Date.now()}`;
      }

      const res = await fetch(url, {
        method: "GET",
        headers,
        cache: "no-store",
        credentials: "include",
      });

      const contentType = res.headers.get("content-type") || "";
      const rawText = await res.text();

      if (!res.ok) {
        clearTokenState();
        setTokenError(`Token request failed: ${res.status}`);
        return;
      }

      if (!contentType.toLowerCase().includes("application/json")) {
        clearTokenState();
        setTokenError("Token endpoint did not return JSON.");
        return;
      }

      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        clearTokenState();
        setTokenError("Token response is not valid JSON.");
        return;
      }

      const tok = data?.token ? String(data.token) : "";
      const branch = data?.branch ? String(data.branch).trim().toLowerCase() : "";
      const exp = Number(data?.expires_in || 0);
      const win = Number(data?.window_seconds || 0);
      const rt = data?.reset_time ? String(data.reset_time) : "23:59:59";
      const rm = data?.reset_mode ? String(data.reset_mode) : "daily";

      if (!tok || !branch || !(exp > 0)) {
        clearTokenState();
        setTokenError("Token response is incomplete.");
        return;
      }

      setMenuToken(tok);
      setResolvedBranch(branch);
      setTokenExpiresIn(exp);
      setTokenWindowSeconds(win > 0 ? win : 0);
      setResetTime(rt);
      setResetMode(rm);
    } catch (e) {
      clearTokenState();
      setTokenError(`Network error: ${e?.message || String(e)}`);
    } finally {
      setTokenLoading(false);
    }
  }, [
    open,
    apiBase,
    branchCode,
    serverLocked,
    hostLockedBranch,
    clearTokenState,
  ]);

  const qrUrl = useMemo(() => {
    return buildMenuFileUrl(resolvedBranch, menuToken);
  }, [buildMenuFileUrl, resolvedBranch, menuToken]);

  const qrReady = !!menuToken && !!resolvedBranch && tokenExpiresIn > 0 && !!qrUrl;

  useEffect(() => {
    if (!open) return;
    clearTokenState();
    setTokenError("");
    fetchMenuToken();
  }, [open, clearTokenState, fetchMenuToken]);

  useEffect(() => {
    if (!open) return;
    if (serverLocked) return;
    if (!branchCode) return;
    fetchMenuToken();
  }, [branchCode, open, serverLocked, fetchMenuToken]);

  useEffect(() => {
    if (!open) return;

    if (tokenRefreshRef.current) {
      clearTimeout(tokenRefreshRef.current);
      tokenRefreshRef.current = null;
    }

    const sec = Number(tokenExpiresIn || 0);

    if (sec > 0) {
      const refreshMs = Math.max(5000, (sec - 30) * 1000);
      tokenRefreshRef.current = setTimeout(() => {
        fetchMenuToken();
      }, refreshMs);
    } else {
      tokenRefreshRef.current = setTimeout(() => {
        fetchMenuToken();
      }, 30000);
    }

    return () => {
      if (tokenRefreshRef.current) {
        clearTimeout(tokenRefreshRef.current);
        tokenRefreshRef.current = null;
      }
    };
  }, [open, tokenExpiresIn, fetchMenuToken]);

  useEffect(() => {
    if (tokenTimerRef.current) {
      clearInterval(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }

    if (!open) return;

    tokenTimerRef.current = setInterval(() => {
      setTokenExpiresIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    return () => {
      if (tokenTimerRef.current) {
        clearInterval(tokenTimerRef.current);
        tokenTimerRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      document.body.classList.remove("qr-open");
      return;
    }

    document.body.classList.add("qr-open");

    return () => {
      document.body.classList.remove("qr-open");
      clearTimers();
    };
  }, [open, clearTimers]);

  const openMenuNow = useCallback(() => {
    if (!qrUrl) return;
    window.open(qrUrl, "_blank", "noopener,noreferrer");
  }, [qrUrl]);

  if (!open) return null;

  return (
    <div
      className="qr-overlay"
      onClick={() => onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label="Menu QR modal"
    >
      <div className="qr-sheet qr-sheet-customer" onClick={(e) => e.stopPropagation()}>
        <button className="qr-close" onClick={() => onClose?.()} aria-label="Close QR">
          ✖
        </button>

        <div className="qr-customer-wrap">
          <div className="qr-customer-header">
            <div className="qr-title">Scan to Open Menu</div>
            <div className="qr-subtitle">{activeBranchLabel}</div>
          </div>

          <div className="qr-customer-instruction">
            Point your phone camera at the QR code
          </div>

          <div className="qr-customer-box">
            {qrReady ? (
              <QRCodeCanvas value={qrUrl} size={qrSize} includeMargin />
            ) : (
              <div className="qr-loading-box">
                {tokenLoading ? "Preparing secure menu..." : tokenError || "Preparing secure menu..."}
              </div>
            )}
          </div>

          {qrReady && (
            <>
              <button className="qr-open-btn" type="button" onClick={openMenuNow}>
                Open Menu
              </button>

              <div className="qr-timer">
                Link expires in {formatExpires(tokenExpiresIn)}
                {resetMode === "daily" ? ` and resets daily at ${resetTime}` : ""}
              </div>

              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: "rgba(255,255,255,.7)",
                  wordBreak: "break-all",
                  textAlign: "center",
                }}
              >
                {qrUrl}
              </div>
            </>
          )}

          {!qrReady && !tokenLoading && tokenError && (
            <button className="qr-retry-btn" type="button" onClick={fetchMenuToken}>
              Try Again
            </button>
          )}

          {!serverLocked &&
            typeof setBranchCode === "function" &&
            BRANCH_OPTIONS.length > 0 && (
              <div className="qr-branch-row">
                <div className="qr-branch-label">Branch</div>
                <select
                  className="qr-branch-select"
                  value={branchCode || getActiveBranch() || "jounieh"}
                  onChange={(e) => setBranchCode(e.target.value)}
                >
                  {BRANCH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}